import { useState, useEffect, useCallback } from "react";
import {
  Calendar, Plus, Edit, Trash2, Search, Save, Loader2, 
  Trophy, Users, Clock, MapPin, CheckCircle, Play, Image, X, Upload, Facebook,
  Radio, Minus, RefreshCw, Timer, Zap, Camera
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";
import LiveClock from "@/components/common/LiveClock";
import { getMatchClock } from "@/hooks/useMatchClock";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const safeStr = dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr;
  return new Date(safeStr).toLocaleDateString("es-HN", { day: "numeric", month: "short", year: "numeric" });
};

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Programado", color: "bg-blue-100 text-blue-700" },
  { value: "live", label: "En Vivo", color: "bg-red-100 text-red-700" },
  { value: "finished", label: "Finalizado", color: "bg-green-100 text-green-700" },
  { value: "suspended", label: "Suspendido", color: "bg-yellow-100 text-yellow-700" },
  { value: "postponed", label: "Aplazado", color: "bg-gray-100 text-gray-700" },
];


// Photo Gallery Modal Component
function PhotoGalleryModal({ match, isOpen, onClose, onPhotosUpdated }) {
  const [photos, setPhotos] = useState(match?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    setPhotos(match?.photos || []);
  }, [match]);

  // Build match data for API update (only fields in MatchBase)
  const buildMatchData = (newPhotos) => ({
    tournament_id: match.tournament_id,
    matchday_id: match.matchday_id || null,
    home_team_id: match.home_team_id,
    away_team_id: match.away_team_id,
    home_score: match.home_score,
    away_score: match.away_score,
    match_date: match.match_date,
    match_time: match.match_time || null,
    stadium: match.stadium || null,
    status: match.status,
    minute: match.minute || null,
    photos: newPhotos,
    facebook_link: match.facebook_link || null
  });

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newPhotos = [...photos];
    const token = localStorage.getItem("session_token");
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(`${API}/media/upload`, formData, {
          headers: { 
            "Content-Type": "multipart/form-data",
            ...(token && { "Authorization": `Bearer ${token}` })
          }
        });
        
        if (res.data.url) {
          newPhotos.push({
            url: res.data.url,
            uploaded_at: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error("Error uploading:", error);
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    // Save to match
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, buildMatchData(newPhotos));
      
      setPhotos(newPhotos);
      toast.success(`${files.length} foto(s) subida(s)`);
      onPhotosUpdated();
    } catch (error) {
      console.error("Error saving photos:", error);
      toast.error("Error guardando fotos");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (index) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    
    const newPhotos = photos.filter((_, i) => i !== index);
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, buildMatchData(newPhotos));
      
      setPhotos(newPhotos);
      toast.success("Foto eliminada");
      onPhotosUpdated();
    } catch (error) {
      toast.error("Error eliminando foto");
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="h-5 w-5 text-primary" />
            Fotos del Partido
          </DialogTitle>
        </DialogHeader>
        
        {/* Match Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              {match?.home_team?.logo_url && (
                <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-12 h-12 object-contain mx-auto mb-1" />
              )}
              <span className="font-medium text-sm">{match?.home_team?.name}</span>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {match?.home_score ?? 0} - {match?.away_score ?? 0}
              </div>
              {/* Live Clock */}
              {match?.status === "live" && (
                <div className="mt-2">
                  <LiveClock match={match} size="md" />
                </div>
              )}
            </div>
            <div className="text-center">
              {match?.away_team?.logo_url && (
                <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-12 h-12 object-contain mx-auto mb-1" />
              )}
              <span className="font-medium text-sm">{match?.away_team?.name}</span>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-2">
            {formatDate(match?.match_date)}
          </p>
        </div>
        
        {/* Upload Section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="photo-upload"
            disabled={uploading}
          />
          <label
            htmlFor="photo-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            {uploading ? (
              <Loader2 className="h-10 w-10 text-primary animate-spin mb-2" />
            ) : (
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
            )}
            <p className="text-sm text-gray-600">
              {uploading ? "Subiendo fotos..." : "Clic para subir fotos"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              JPG, PNG, WebP (múltiples archivos)
            </p>
          </label>
        </div>
        
        {/* Photos Grid */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="relative group aspect-square">
                <img
                  src={getImageUrl(photo.url)}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90"
                  onClick={() => setSelectedPhoto(photo)}
                />
                <button
                  onClick={() => handleDeletePhoto(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No hay fotos para este partido</p>
          </div>
        )}
        
        {/* Full Image Preview */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white p-2"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={getImageUrl(selectedPhoto.url)}
              alt="Vista completa"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MatchForm({ match, tournaments, teams, matchdays, onSave, onClose }) {
  const [formData, setFormData] = useState({
    tournament_id: match?.tournament_id || "",
    matchday_id: match?.matchday_id || "",
    home_team_id: match?.home_team_id || "",
    away_team_id: match?.away_team_id || "",
    home_score: match?.home_score ?? null,
    away_score: match?.away_score ?? null,
    match_date: match?.match_date || new Date().toISOString().split("T")[0],
    match_time: match?.match_time || "",
    stadium: match?.stadium || "",
    status: match?.status || "scheduled",
    minute: match?.minute || null,
    facebook_link: match?.facebook_link || "",
  });
  const [saving, setSaving] = useState(false);
  const [filteredMatchdays, setFilteredMatchdays] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);

  useEffect(() => {
    if (formData.tournament_id) {
      const tournament = tournaments.find(t => t.tournament_id === formData.tournament_id);
      setFilteredMatchdays(matchdays.filter(m => m.tournament_id === formData.tournament_id));
      setFilteredTeams(teams.filter(t => tournament?.team_ids?.includes(t.team_id)));
    }
  }, [formData.tournament_id, tournaments, matchdays, teams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tournament_id || !formData.home_team_id || !formData.away_team_id || !formData.match_date) {
      toast.error("Complete los campos obligatorios");
      return;
    }
    if (formData.home_team_id === formData.away_team_id) {
      toast.error("Los equipos deben ser diferentes");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Tournament */}
      <div>
        <Label>Torneo *</Label>
        <Select value={formData.tournament_id} onValueChange={(v) => setFormData({ ...formData, tournament_id: v, matchday_id: "", home_team_id: "", away_team_id: "" })}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar torneo" /></SelectTrigger>
          <SelectContent>
            {tournaments.map((t) => (
              <SelectItem key={t.tournament_id} value={t.tournament_id}>{t.name} - {t.season}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Matchday */}
      {filteredMatchdays.length > 0 && (
        <div>
          <Label>Jornada</Label>
          <Select value={formData.matchday_id || "none"} onValueChange={(v) => setFormData({ ...formData, matchday_id: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar jornada" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sin jornada</SelectItem>
              {filteredMatchdays.map((m) => (
                <SelectItem key={m.matchday_id} value={m.matchday_id}>{m.name || `Jornada ${m.number}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Teams */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Equipo Local *</Label>
          <Select value={formData.home_team_id} onValueChange={(v) => setFormData({ ...formData, home_team_id: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Local" /></SelectTrigger>
            <SelectContent>
              {filteredTeams.map((t) => (
                <SelectItem key={t.team_id} value={t.team_id} disabled={t.team_id === formData.away_team_id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Equipo Visitante *</Label>
          <Select value={formData.away_team_id} onValueChange={(v) => setFormData({ ...formData, away_team_id: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Visitante" /></SelectTrigger>
            <SelectContent>
              {filteredTeams.map((t) => (
                <SelectItem key={t.team_id} value={t.team_id} disabled={t.team_id === formData.home_team_id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fecha *</Label>
          <Input type="date" value={formData.match_date} onChange={(e) => setFormData({ ...formData, match_date: e.target.value })} className="mt-1" required />
        </div>
        <div>
          <Label>Hora</Label>
          <Input type="time" value={formData.match_time} onChange={(e) => setFormData({ ...formData, match_time: e.target.value })} className="mt-1" />
        </div>
      </div>

      {/* Stadium */}
      <div>
        <Label>Estadio</Label>
        <Input value={formData.stadium} onChange={(e) => setFormData({ ...formData, stadium: e.target.value })} placeholder="Ej: Estadio Nacional" className="mt-1" />
      </div>

      {/* Facebook Link */}
      <div>
        <Label className="flex items-center gap-2">
          <Facebook className="h-4 w-4 text-blue-600" />
          Enlace de Facebook (transmisión en vivo)
        </Label>
        <Input 
          value={formData.facebook_link} 
          onChange={(e) => setFormData({ ...formData, facebook_link: e.target.value })} 
          placeholder="Ej: https://www.facebook.com/watch/live/?ref=watch_permalink&v=123456" 
          className="mt-1" 
        />
        <p className="text-xs text-gray-500 mt-1">Enlace de transmisión en vivo o video de Facebook</p>
      </div>

      {/* Status & Score */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Estado</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Goles Local</Label>
          <Input type="number" value={formData.home_score ?? ""} onChange={(e) => setFormData({ ...formData, home_score: e.target.value === "" ? null : parseInt(e.target.value) })} className="mt-1" min="0" />
        </div>
        <div>
          <Label>Goles Visitante</Label>
          <Input type="number" value={formData.away_score ?? ""} onChange={(e) => setFormData({ ...formData, away_score: e.target.value === "" ? null : parseInt(e.target.value) })} className="mt-1" min="0" />
        </div>
      </div>

      {/* Minute (for live) */}
      {formData.status === "live" && (
        <div>
          <Label>Minuto</Label>
          <Input type="number" value={formData.minute ?? ""} onChange={(e) => setFormData({ ...formData, minute: e.target.value === "" ? null : parseInt(e.target.value) })} className="mt-1" min="0" max="120" placeholder="Ej: 45" />
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={saving}>
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : <><Save className="h-4 w-4 mr-2" />Guardar</>}
        </Button>
      </div>
    </form>
  );
}

function MatchdayForm({ matchday, tournaments, onSave, onClose }) {
  const [formData, setFormData] = useState({
    tournament_id: matchday?.tournament_id || "",
    number: matchday?.number || 1,
    name: matchday?.name || "",
    start_date: matchday?.start_date || "",
    end_date: matchday?.end_date || "",
    status: matchday?.status || "open",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tournament_id || !formData.number) {
      toast.error("Torneo y número son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Torneo *</Label>
        <Select value={formData.tournament_id} onValueChange={(v) => setFormData({ ...formData, tournament_id: v })}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar torneo" /></SelectTrigger>
          <SelectContent>
            {tournaments.map((t) => (
              <SelectItem key={t.tournament_id} value={t.tournament_id}>{t.name} - {t.season}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Número *</Label>
          <Input type="number" value={formData.number} onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) || 1 })} className="mt-1" min="1" />
        </div>
        <div>
          <Label>Nombre</Label>
          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ej: Jornada 1" className="mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fecha Inicio</Label>
          <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>Fecha Fin</Label>
          <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="mt-1" />
        </div>
      </div>
      <div>
        <Label>Estado</Label>
        <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Abierta</SelectItem>
            <SelectItem value="closed">Cerrada</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={saving}>
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : <><Save className="h-4 w-4 mr-2" />Guardar</>}
        </Button>
      </div>
    </form>
  );
}

// Live Match Control Panel - Quick editing for live matches with auto-clock
function LiveMatchPanel({ matches, tournaments, teams, onUpdate, onRefresh }) {
  const [updating, setUpdating] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  
  const liveMatches = matches.filter(m => m.status === "live");
  
  // Group live matches by tournament
  const matchesByTournament = liveMatches.reduce((acc, match) => {
    const tournamentId = match.tournament_id;
    if (!acc[tournamentId]) {
      acc[tournamentId] = [];
    }
    acc[tournamentId].push(match);
    return acc;
  }, {});

  const handleScoreChange = async (match, team, delta) => {
    const key = `${match.match_id}-score`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    const newHomeScore = team === 'home' 
      ? Math.max(0, (match.home_score || 0) + delta)
      : match.home_score || 0;
    const newAwayScore = team === 'away'
      ? Math.max(0, (match.away_score || 0) + delta)
      : match.away_score || 0;
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: newHomeScore,
        away_score: newAwayScore,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: match.minute || null,
        minute_started_at: match.minute_started_at || null,
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success(`Marcador actualizado: ${newHomeScore} - ${newAwayScore}`);
      onUpdate();
    } catch (error) {
      toast.error("Error al actualizar marcador");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleMinuteChange = async (match, newMinute) => {
    const key = `${match.match_id}-minute`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    // Set minute_started_at to now when minute is manually selected
    const minuteStartedAt = new Date().toISOString();
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: newMinute,
        minute_started_at: minuteStartedAt,
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success(`Reloj iniciado desde ${newMinute}'`);
      onUpdate();
    } catch (error) {
      toast.error("Error al actualizar minuto");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePauseClock = async (match) => {
    const key = `${match.match_id}-pause`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    // Get current calculated minute and save it (stops the auto-clock)
    const currentMinute = getMatchClock(match).totalMinute || match.minute || 0;
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: currentMinute,
        minute_started_at: null, // Clear to pause the clock
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success(`Reloj pausado en ${currentMinute}'`);
      onUpdate();
    } catch (error) {
      toast.error("Error al pausar reloj");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleResumeClock = async (match) => {
    const key = `${match.match_id}-resume`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    // Resume from current minute
    const minuteStartedAt = new Date().toISOString();
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: match.minute || 0,
        minute_started_at: minuteStartedAt,
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success(`Reloj reanudado desde ${match.minute || 0}'`);
      onUpdate();
    } catch (error) {
      toast.error("Error al reanudar reloj");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleEndMatch = async (match) => {
    if (!window.confirm(`¿Finalizar el partido ${match.home_team?.name} vs ${match.away_team?.name}?`)) return;
    
    const key = `${match.match_id}-end`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: "finished",
        minute: 90,
        minute_started_at: null,
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success("Partido finalizado");
      onUpdate();
    } catch (error) {
      toast.error("Error al finalizar partido");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Handle Half Time - pause clock and set to "HT" state
  const handleHalfTime = async (match) => {
    const key = `${match.match_id}-halftime`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: 45,
        minute_started_at: null, // Pause the clock
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success("MEDIO TIEMPO - Reloj pausado en 45'");
      onUpdate();
    } catch (error) {
      toast.error("Error al establecer medio tiempo");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Handle Second Half - start clock from minute 46
  const handleSecondHalf = async (match) => {
    const key = `${match.match_id}-secondhalf`;
    setUpdating(prev => ({ ...prev, [key]: true }));
    
    const minuteStartedAt = new Date().toISOString();
    
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: 46,
        minute_started_at: minuteStartedAt, // Start the clock
        photos: match.photos || null,
        facebook_link: match.facebook_link || null
      });
      
      toast.success("SEGUNDA PARTE - Reloj iniciado en 46'");
      onUpdate();
    } catch (error) {
      toast.error("Error al iniciar segunda parte");
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Photo upload handler for live matches
  const handlePhotoUpload = async (match, e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const key = `${match.match_id}-photos`;
    setUploadingPhotos(prev => ({ ...prev, [key]: true }));
    
    const token = localStorage.getItem("session_token");
    const newPhotos = [...(match.photos || [])];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(`${API}/media/upload`, formData, {
          headers: { 
            "Content-Type": "multipart/form-data",
            ...(token && { "Authorization": `Bearer ${token}` })
          }
        });
        
        if (res.data.url) {
          newPhotos.push({
            url: res.data.url,
            uploaded_at: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error("Error uploading:", error);
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    // Save photos to match
    try {
      await axios.put(`${API}/sports/matches/${match.match_id}`, {
        tournament_id: match.tournament_id,
        matchday_id: match.matchday_id || null,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_score: match.home_score,
        away_score: match.away_score,
        match_date: match.match_date,
        match_time: match.match_time || null,
        stadium: match.stadium || null,
        status: match.status,
        minute: match.minute,
        minute_started_at: match.minute_started_at || null,
        photos: newPhotos,
        facebook_link: match.facebook_link || null
      });
      
      toast.success(`${files.length} foto(s) subida(s)`);
      onUpdate();
    } catch (error) {
      toast.error("Error guardando fotos");
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [key]: false }));
    }
  };

  if (liveMatches.length === 0) {
    return (
      <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
        <Radio className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-500 mb-2">No hay partidos en vivo</h3>
        <p className="text-gray-400">Cuando un partido esté "En Vivo" aparecerá aquí para edición rápida</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Radio className="h-6 w-6 text-red-500" />
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {liveMatches.length} Partido{liveMatches.length > 1 ? 's' : ''} en Vivo
            </h2>
            <p className="text-sm text-gray-500">Edición rápida de marcadores y reloj automático</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {/* Live matches grouped by tournament */}
      {Object.entries(matchesByTournament).map(([tournamentId, tournamentMatches]) => {
        const tournament = tournaments.find(t => t.tournament_id === tournamentId);
        
        return (
          <div key={tournamentId} className="bg-white rounded-2xl border-2 border-red-100 overflow-hidden shadow-sm">
            {/* Tournament Header */}
            <div 
              className="px-4 py-3 border-b flex items-center gap-3"
              style={{ backgroundColor: tournament?.color || '#10B981', color: 'white' }}
            >
              <Trophy className="h-5 w-5" />
              <span className="font-bold">{tournament?.name || 'Torneo'}</span>
              <span className="text-sm opacity-80">{tournament?.season}</span>
              <Badge className="ml-auto bg-white/20 text-white border-0">
                {tournamentMatches.length} en vivo
              </Badge>
            </div>

            {/* Matches */}
            <div className="divide-y divide-gray-100">
              {tournamentMatches.map((match) => {
                const isUpdatingScore = updating[`${match.match_id}-score`];
                const isUpdatingMinute = updating[`${match.match_id}-minute`];
                const isEndingMatch = updating[`${match.match_id}-end`];
                const isPausing = updating[`${match.match_id}-pause`];
                const isResuming = updating[`${match.match_id}-resume`];
                const isUploadingPhotos = uploadingPhotos[`${match.match_id}-photos`];
                const isClockRunning = !!match.minute_started_at;
                
                return (
                  <div key={match.match_id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      {/* Home Team */}
                      <div className="flex-1 text-right">
                        <div className="flex items-center justify-end gap-2 mb-2">
                          {match.home_team?.logo_url && (
                            <img 
                              src={getImageUrl(match.home_team.logo_url)} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                          )}
                          <span className="font-bold text-gray-800">{match.home_team?.name}</span>
                        </div>
                        {/* Score Controls - Home */}
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleScoreChange(match, 'home', -1)}
                            disabled={isUpdatingScore || (match.home_score || 0) === 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => handleScoreChange(match, 'home', 1)}
                            disabled={isUpdatingScore}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Score and Clock Display */}
                      <div className="flex flex-col items-center min-w-[140px]">
                        <div className="flex items-center gap-2 text-4xl font-black text-gray-800">
                          {isUpdatingScore ? (
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          ) : (
                            <>
                              <span>{match.home_score ?? 0}</span>
                              <span className="text-gray-300">-</span>
                              <span>{match.away_score ?? 0}</span>
                            </>
                          )}
                        </div>
                        {/* Auto Clock Display with Period (1T, MT, 2T) */}
                        <div className="mt-2">
                          <LiveClock match={match} size="md" />
                        </div>
                        {/* Clock Control Buttons */}
                        <div className="flex items-center gap-1 mt-1">
                          {isClockRunning ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                              onClick={() => handlePauseClock(match)}
                              disabled={isPausing}
                            >
                              {isPausing ? <Loader2 className="h-3 w-3 animate-spin" /> : "⏸ Pausar"}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => handleResumeClock(match)}
                              disabled={isResuming}
                            >
                              {isResuming ? <Loader2 className="h-3 w-3 animate-spin" /> : "▶ Reanudar"}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Away Team */}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-gray-800">{match.away_team?.name}</span>
                          {match.away_team?.logo_url && (
                            <img 
                              src={getImageUrl(match.away_team.logo_url)} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                          )}
                        </div>
                        {/* Score Controls - Away */}
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => handleScoreChange(match, 'away', 1)}
                            disabled={isUpdatingScore}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleScoreChange(match, 'away', -1)}
                            disabled={isUpdatingScore || (match.away_score || 0) === 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions Row */}
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-3 border-t border-gray-100">
                      {/* Minute Presets */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 mr-1">Iniciar en:</span>
                        {[1, 15, 30, 45].map((min) => (
                          <Button
                            key={min}
                            size="sm"
                            variant={match.minute === min && !match.minute_started_at ? "default" : "outline"}
                            className="h-7 w-10 p-0 text-xs"
                            onClick={() => handleMinuteChange(match, min)}
                            disabled={isUpdatingMinute}
                          >
                            {min}'
                          </Button>
                        ))}
                        
                        {/* Half Time Button */}
                        <Button
                          size="sm"
                          variant={match.minute === 45 && !match.minute_started_at ? "default" : "outline"}
                          className="h-7 px-2 text-xs bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100 font-bold"
                          onClick={() => handleHalfTime(match)}
                          disabled={updating[`${match.match_id}-halftime`]}
                        >
                          {updating[`${match.match_id}-halftime`] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "MT"
                          )}
                        </Button>
                        
                        {/* Second Half Button */}
                        <Button
                          size="sm"
                          variant={match.minute === 46 && match.minute_started_at ? "default" : "outline"}
                          className="h-7 px-2 text-xs bg-green-50 border-green-400 text-green-700 hover:bg-green-100 font-bold"
                          onClick={() => handleSecondHalf(match)}
                          disabled={updating[`${match.match_id}-secondhalf`]}
                        >
                          {updating[`${match.match_id}-secondhalf`] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "2T"
                          )}
                        </Button>
                        
                        {[46, 60, 75, 90].map((min) => (
                          <Button
                            key={min}
                            size="sm"
                            variant={match.minute === min && !match.minute_started_at ? "default" : "outline"}
                            className="h-7 w-10 p-0 text-xs"
                            onClick={() => handleMinuteChange(match, min)}
                            disabled={isUpdatingMinute}
                          >
                            {min}'
                          </Button>
                        ))}
                      </div>
                      
                      {/* Divider */}
                      <div className="h-6 w-px bg-gray-200 mx-2" />
                      
                      {/* Photo Upload */}
                      <div className="flex items-center gap-1">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(match, e)}
                          className="hidden"
                          id={`photo-upload-${match.match_id}`}
                          disabled={isUploadingPhotos}
                        />
                        <label
                          htmlFor={`photo-upload-${match.match_id}`}
                          className={`inline-flex items-center h-7 px-2 text-xs border rounded-md cursor-pointer transition-colors ${
                            isUploadingPhotos 
                              ? 'bg-gray-100 text-gray-400 border-gray-200'
                              : 'text-purple-600 border-purple-300 hover:bg-purple-50'
                          }`}
                        >
                          {isUploadingPhotos ? (
                            <><Loader2 className="h-3 w-3 animate-spin mr-1" />Subiendo...</>
                          ) : (
                            <><Camera className="h-3 w-3 mr-1" />Fotos {match.photos?.length > 0 && `(${match.photos.length})`}</>
                          )}
                        </label>
                      </div>
                      
                      {/* Divider */}
                      <div className="h-6 w-px bg-gray-200 mx-2" />
                      
                      {/* End Match Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={() => handleEndMatch(match)}
                        disabled={isEndingMatch}
                      >
                        {isEndingMatch ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        )}
                        Finalizar
                      </Button>
                      
                      {/* Facebook Link if exists */}
                      {match.facebook_link && (
                        <a
                          href={match.facebook_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center h-7 px-2 text-xs text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                        >
                          <Facebook className="h-3 w-3 mr-1" />
                          FB Live
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminPartidos() {
  const [activeTab, setActiveTab] = useState("matches");
  const [matches, setMatches] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState("all");
  const [selectedMatchday, setSelectedMatchday] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // list, calendar
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [showMatchdayForm, setShowMatchdayForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [editingMatchday, setEditingMatchday] = useState(null);
  const [galleryMatch, setGalleryMatch] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [liveMatches, setLiveMatches] = useState([]);

  // Count live matches for tab badge
  const liveMatchesCount = liveMatches.filter(m => m.status === "live").length;

  useEffect(() => {
    fetchData();
  }, [page, limit, selectedTournament, selectedMatchday]);

  const fetchData = async () => {
    try {
      const matchParams = new URLSearchParams();
      matchParams.append("skip", String((page - 1) * limit));
      matchParams.append("limit", String(limit));
      if (selectedTournament !== "all") matchParams.append("tournament_id", selectedTournament);
      if (selectedMatchday !== "all") matchParams.append("matchday_id", selectedMatchday);

      const [matchesRes, liveRes, matchdaysRes, tournamentsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/sports/matches?${matchParams}`),
        axios.get(`${API}/sports/matches?status=live&limit=100`),
        axios.get(`${API}/sports/matchdays?limit=200`),
        axios.get(`${API}/sports/tournaments?status=active&limit=100`),
        axios.get(`${API}/sports/teams?is_active=true&limit=200`),
      ]);
      setMatches(matchesRes.data.items || []);
      setTotal(matchesRes.data.total || 0);
      setLiveMatches(liveRes.data.items || []);
      setMatchdays(matchdaysRes.data.items || []);
      
      // Sort tournaments: primera division first, then segunda
      const sortedTournaments = (tournamentsRes.data.items || []).sort((a, b) => {
        if (a.division === "primera" && b.division !== "primera") return -1;
        if (a.division !== "primera" && b.division === "primera") return 1;
        return 0;
      });
      setTournaments(sortedTournaments);
      
      setTeams(teamsRes.data.items || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMatch = async (data) => {
    if (editingMatch) {
      await axios.put(`${API}/sports/matches/${editingMatch.match_id}`, data);
      toast.success("Partido actualizado");
    } else {
      await axios.post(`${API}/sports/matches`, data);
      toast.success("Partido creado");
    }
    fetchData();
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm("¿Eliminar este partido?")) return;
    try {
      await axios.delete(`${API}/sports/matches/${matchId}`);
      toast.success("Partido eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleSaveMatchday = async (data) => {
    if (editingMatchday) {
      await axios.put(`${API}/sports/matchdays/${editingMatchday.matchday_id}`, data);
      toast.success("Jornada actualizada");
    } else {
      await axios.post(`${API}/sports/matchdays`, data);
      toast.success("Jornada creada");
    }
    fetchData();
  };

  const handleDeleteMatchday = async (matchdayId) => {
    if (!window.confirm("¿Eliminar esta jornada?")) return;
    try {
      await axios.delete(`${API}/sports/matchdays/${matchdayId}`);
      toast.success("Jornada eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredMatches = matches.filter(m => {
    const tournamentMatch = selectedTournament === "all" || m.tournament_id === selectedTournament;
    const matchdayMatch = selectedMatchday === "all" || m.matchday_id === selectedMatchday;
    return tournamentMatch && matchdayMatch;
  });
  const filteredMatchdays = selectedTournament === "all" ? matchdays : matchdays.filter(m => m.tournament_id === selectedTournament);

  // Get matchdays for selected tournament (for filter dropdown)
  const availableMatchdays = selectedTournament === "all" 
    ? matchdays 
    : matchdays.filter(md => md.tournament_id === selectedTournament);

  // Group matches by date for calendar view
  const matchesByDate = filteredMatches.reduce((acc, match) => {
    const date = match.match_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(match);
    return acc;
  }, {});

  // Get all unique dates sorted
  const sortedDates = Object.keys(matchesByDate).sort((a, b) => new Date(a) - new Date(b));

  const getStatusBadge = (status) => {
    const s = STATUS_OPTIONS.find(o => o.value === status);
    return s ? <Badge className={s.color}>{s.label}</Badge> : <Badge>{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" />
              Partidos y Jornadas
            </h1>
            <p className="text-brand-textSecondary">Gestiona los partidos de cada torneo</p>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Tournament Filter */}
          <Select value={selectedTournament} onValueChange={(val) => { setSelectedTournament(val); setSelectedMatchday("all"); }}>
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Filtrar por torneo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los torneos</SelectItem>
              {/* Primera División */}
              {tournaments.filter(t => t.division === "primera").length > 0 && (
                <div className="px-2 py-1 text-xs font-bold text-blue-600 bg-blue-50">Primera División</div>
              )}
              {tournaments.filter(t => t.division === "primera").map((t) => (
                <SelectItem key={t.tournament_id} value={t.tournament_id}>
                  {t.name} - {t.season}
                </SelectItem>
              ))}
              {/* Segunda División */}
              {tournaments.filter(t => t.division !== "primera").length > 0 && (
                <div className="px-2 py-1 text-xs font-bold text-orange-600 bg-orange-50 mt-1">Segunda División</div>
              )}
              {tournaments.filter(t => t.division !== "primera").map((t) => (
                <SelectItem key={t.tournament_id} value={t.tournament_id}>
                  {t.name} - {t.season}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Matchday Filter */}
          <Select value={selectedMatchday} onValueChange={setSelectedMatchday}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Filtrar por jornada" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las jornadas</SelectItem>
              {availableMatchdays.map((md) => (
                <SelectItem key={md.matchday_id} value={md.matchday_id}>
                  {md.name} {md.round && `(R${md.round})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 ml-auto bg-gray-100 rounded-lg p-1">
            <Button
              size="sm"
              variant={viewMode === "list" ? "default" : "ghost"}
              className="h-8 px-3"
              onClick={() => setViewMode("list")}
            >
              <Users className="h-4 w-4 mr-1" />
              Lista
            </Button>
            <Button
              size="sm"
              variant={viewMode === "calendar" ? "default" : "ghost"}
              className="h-8 px-3"
              onClick={() => setViewMode("calendar")}
            >
              <Calendar className="h-4 w-4 mr-1" />
              Calendario
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="live" className="relative">
              <Radio className="h-4 w-4 mr-1" />
              En Vivo
              {liveMatchesCount > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                  {liveMatchesCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="matches">Partidos ({total})</TabsTrigger>
            <TabsTrigger value="matchdays">Jornadas ({filteredMatchdays.length})</TabsTrigger>
          </TabsList>

          {/* Live Matches Tab */}
          <TabsContent value="live" className="space-y-4">
            <LiveMatchPanel 
              matches={liveMatches}
              tournaments={tournaments}
              teams={teams}
              onUpdate={fetchData}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setEditingMatch(null); setShowMatchForm(true); }}>
                <Plus className="h-4 w-4 mr-2" />Nuevo Partido
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : filteredMatches.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No hay partidos</h3>
              </div>
            ) : viewMode === "calendar" ? (
              /* Calendar View */
              <div className="space-y-4">
                {sortedDates.map((date) => {
                  const dateMatches = matchesByDate[date];
                  const dateObj = new Date(date + 'T12:00:00');
                  const dayName = dateObj.toLocaleDateString('es-HN', { weekday: 'long' });
                  const monthDay = dateObj.toLocaleDateString('es-HN', { day: 'numeric', month: 'short' });
                  
                  return (
                    <div key={date} className="bg-white rounded-xl border overflow-hidden">
                      {/* Date Header */}
                      <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-white">
                          <Calendar className="h-5 w-5" />
                          <span className="font-bold capitalize">{dayName}</span>
                          <span className="text-white/80">{monthDay}</span>
                        </div>
                        <Badge className="bg-white/20 text-white border-0">
                          {dateMatches.length} partido{dateMatches.length > 1 ? 's' : ''}
                        </Badge>
                      </div>
                      
                      {/* Matches for this date */}
                      <div className="divide-y divide-gray-100">
                        {dateMatches.map((match) => {
                          const tournament = tournaments.find(t => t.tournament_id === match.tournament_id);
                          const matchday = matchdays.find(md => md.matchday_id === match.matchday_id);
                          
                          return (
                            <div key={match.match_id} className="p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-4">
                                {/* Time */}
                                <div className="w-16 text-center">
                                  <span className="text-lg font-bold text-gray-700">{match.match_time || "--:--"}</span>
                                </div>
                                
                                {/* Tournament & Matchday Info */}
                                <div className="w-40 hidden md:block">
                                  <p className="text-xs font-medium text-gray-600 truncate">{tournament?.name}</p>
                                  {matchday && <p className="text-xs text-gray-400">{matchday.name}</p>}
                                </div>
                                
                                {/* Home Team */}
                                <div className="flex-1 flex items-center justify-end gap-2">
                                  <span className="font-medium text-right">{match.home_team?.name}</span>
                                  {match.home_team?.logo_url && (
                                    <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
                                  )}
                                </div>
                                
                                {/* Score */}
                                <div className="w-24 text-center">
                                  {match.status === "live" ? (
                                    <div className="bg-red-100 rounded-lg py-1">
                                      <span className="text-xl font-black text-red-600">
                                        {match.home_score ?? 0} - {match.away_score ?? 0}
                                      </span>
                                      <p className="text-xs text-red-500 font-medium animate-pulse">EN VIVO</p>
                                    </div>
                                  ) : match.status === "finished" ? (
                                    <span className="text-xl font-bold text-gray-800">
                                      {match.home_score} - {match.away_score}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">vs</span>
                                  )}
                                </div>
                                
                                {/* Away Team */}
                                <div className="flex-1 flex items-center gap-2">
                                  {match.away_team?.logo_url && (
                                    <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-8 h-8 object-contain" />
                                  )}
                                  <span className="font-medium">{match.away_team?.name}</span>
                                </div>
                                
                                {/* Status & Actions */}
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(match.status)}
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => { setEditingMatch(match); setShowMatchForm(true); }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Stadium */}
                              {match.stadium && (
                                <div className="mt-2 flex items-center gap-1 text-xs text-gray-400 ml-20">
                                  <MapPin className="h-3 w-3" />
                                  {match.stadium}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="bg-white rounded-xl border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead className="text-center">Marcador</TableHead>
                      <TableHead>Visitante</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMatches.map((match) => (
                      <TableRow key={match.match_id}>
                        <TableCell>
                          <div className="text-sm">
                            <p>{formatDate(match.match_date)}</p>
                            {match.match_time && <p className="text-gray-500">{match.match_time}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {match.home_team?.logo_url && <img src={getImageUrl(match.home_team.logo_url)} alt="" className="w-6 h-6 object-contain" />}
                            <span className="font-medium">{match.home_team?.name || "Local"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-bold text-lg">
                          {match.home_score ?? "-"} - {match.away_score ?? "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {match.away_team?.logo_url && <img src={getImageUrl(match.away_team.logo_url)} alt="" className="w-6 h-6 object-contain" />}
                            <span className="font-medium">{match.away_team?.name || "Visitante"}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(match.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {match.facebook_link && (
                              <a
                                href={match.facebook_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-blue-300 text-blue-600 hover:bg-blue-50"
                                title="Ver transmisión en Facebook"
                              >
                                <Facebook className="h-4 w-4" />
                              </a>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => setGalleryMatch(match)}
                              className={match.photos?.length > 0 ? "text-green-600 border-green-300 hover:bg-green-50" : "text-orange-500 border-orange-300 hover:bg-orange-50"}
                              title="Gestionar fotos del partido"
                            >
                              <Camera className="h-4 w-4 mr-1" />
                              {match.photos?.length > 0 ? (
                                <span className="text-xs">{match.photos.length}</span>
                              ) : (
                                <span className="text-xs">Fotos</span>
                              )}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setEditingMatch(match); setShowMatchForm(true); }} title="Editar partido">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteMatch(match.match_id)} title="Eliminar partido">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Matchdays Tab */}
          <TabsContent value="matchdays" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setEditingMatchday(null); setShowMatchdayForm(true); }}>
                <Plus className="h-4 w-4 mr-2" />Nueva Jornada
              </Button>
            </div>

            {filteredMatchdays.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No hay jornadas</h3>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMatchdays.map((md) => {
                  const tournament = tournaments.find(t => t.tournament_id === md.tournament_id);
                  return (
                    <div key={md.matchday_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-brand-text">{md.name || `Jornada ${md.number}`}</h3>
                          <p className="text-sm text-gray-500">{tournament?.name}</p>
                        </div>
                        <Badge className={md.status === "open" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                          {md.status === "open" ? "Abierta" : "Cerrada"}
                        </Badge>
                      </div>
                      {(md.start_date || md.end_date) && (
                        <p className="text-xs text-gray-400 mb-3">
                          {md.start_date && formatDate(md.start_date)} {md.end_date && `- ${formatDate(md.end_date)}`}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditingMatchday(md); setShowMatchdayForm(true); }}>
                          <Edit className="h-4 w-4 mr-1" />Editar
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteMatchday(md.matchday_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Match Form Dialog */}
        <Dialog open={showMatchForm} onOpenChange={setShowMatchForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingMatch ? "Editar Partido" : "Nuevo Partido"}</DialogTitle>
            </DialogHeader>
            <MatchForm
              match={editingMatch}
              tournaments={tournaments}
              teams={teams}
              matchdays={matchdays}
              onSave={handleSaveMatch}
              onClose={() => { setShowMatchForm(false); setEditingMatch(null); }}
            />
          </DialogContent>
        </Dialog>

        {/* Matchday Form Dialog */}
        <Dialog open={showMatchdayForm} onOpenChange={setShowMatchdayForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingMatchday ? "Editar Jornada" : "Nueva Jornada"}</DialogTitle>
            </DialogHeader>
            <MatchdayForm
              matchday={editingMatchday}
              tournaments={tournaments}
              onSave={handleSaveMatchday}
              onClose={() => { setShowMatchdayForm(false); setEditingMatchday(null); }}
            />
          </DialogContent>
        </Dialog>

        {/* Photo Gallery Modal */}
        <PhotoGalleryModal
          match={galleryMatch}
          isOpen={!!galleryMatch}
          onClose={() => setGalleryMatch(null)}
          onPhotosUpdated={fetchData}
        />
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="partidos-pagination"
          label="partidos"
        />
    </AdminLayout>
  );
}
